import React from 'react';
import {
    getRibbonBottomBarAriaControlsId,
    getRibbonBottomBarId,
    menuTextChevronClass,
    RibbonBottomBar,
    type ControlInMenuRendererFunction,
    type ControlRendererFunction,
    type RibbonBottomBarClassNames,
    type RibbonModeToggleProps,
    type RibbonPaddleProps,
    type RibbonTabDefinitionMLR,
    type RibbonTabDefinitionSLR,
} from 'owa-acui';
import { appIcons, getAppStrings } from 'owa-acui-theme';
import {
    bottomBarContainerV8,
    bottomBarContainerV8BleedThrough,
    bottomBarV8,
    chevronContainerV8,
    rootV8,
    tabContentContainerV8,
} from './CommandRibbonBottomBar.scss';
import type { AppPaneUnderlayViewState } from 'owa-app-pane-underlay';
import { type CommandingViewMode } from 'owa-outlook-service-option-store/lib/store/schema/options/CommandingOptions';
import { FluentIconStyleProvider } from 'owa-fluent-icons-svg';
import { RibbonModeMenuContainer } from './RibbonModeMenuContainer';
import { onViewModeChanged } from 'owa-command-ribbon-store';
import isAppPaneUnderlayExpanded from 'owa-app-pane-underlay/lib/utils/isAppPaneUnderlayExpanded';
import { isFlexPaneExpanded } from 'owa-suite-header-store';
import { observer } from 'owa-mobx-react';
import { useControlInMenuRendererFunction } from '../util/useControlInMenuRendererFunction';
import { isSingleLineRibbon } from 'owa-command-ribbon-store/lib/selectors/isSingleLineRibbon';
import ChevronDown from 'owa-fluent-icons-svg/lib/icons/ChevronDownRegular';
import { isFeatureEnabled } from 'owa-feature-flags';
import { tokens, makeStyles, toggleButtonClassNames } from '@fluentui/react-components';
import { getIsDarkTheme } from 'owa-fabric-theme';
import { getIsCommandCenterVisible } from 'owa-copilot-command-center-store';
import classnames from 'owa-classnames';
import { registerAllLightIcons } from 'owa-command-ribbon-light-icon-font';
import { useBlockPlt } from 'owa-react-hooks/lib/useBlockPlt';
import { isCapabilityEnabled } from 'owa-capabilities';
import { bleedThroughCapability } from 'owa-capabilities-definitions/lib/bleedThroughCapability';
import { getRibbonBottomBarStyleConstants } from '../util/getRibbonStyleConstants';
/* eslint-disable-next-line @typescript-eslint/no-restricted-imports -- (https://aka.ms/OWALintWiki)
 * Remove exception when import is available from @fluentui/react-components Work item 384079
 *	> '@fluentui/react-tabster' import is restricted from being used. Import Fluent v9 components from '@fluentui/react-components' */
import { useTabsterAttributes } from '@fluentui/react-tabster';

const iconStyles = {
    rootStyle: {
        height: '100%',
        textAlign: 'center',
        verticalAlign: 'top',
        width: '100%',
    },
};

// Overwriting styles that `FluentIconStyleProvider` applies
const iconStylesFluentV9 = {
    iconStyle: {
        contain: 'inherit',
        height: 'auto',
        width: 'auto',
    },
    rootStyle: {
        contain: 'inherit',
        display: 'inherit',
        height: 'inherit',
        textAlign: 'center',
        verticalAlign: 'inherit',
        width: 'inherit',
    },
};

const ribbonModeToggleProps: RibbonModeToggleProps = {
    isRibbonModeToggleDisabled: false,
    onRibbonModeToggle: () => {
        // registerAllLightIcons receives a parameter that indicates whether the ribbon is going to be switched to MLR, if so we should load the light icons
        // SLR switching to MLR returns true in isSingleLineRibbon
        // MLR switching to SLR returns false in isSingleLineRibbon
        registerAllLightIcons(isSingleLineRibbon());
        onViewModeChanged(isSingleLineRibbon() ? 2 : 1);
    },
    ribbonModeToggleIcon: ChevronDown,
};

const customStylesV8: Partial<RibbonBottomBarClassNames> = {
    chevronContainer: chevronContainerV8,
    root: rootV8,
    tabContentContainer: tabContentContainerV8,
};

const useStyles = makeStyles({
    bottomBar: {
        display: 'flex',
    },
    bottomBarContainer: {
        flexGrow: '1',
        minWidth: '0',

        // Overwriting `menuTextChevronClass` styles defined in 1JS here:
        // https://office.visualstudio.com/Office/_git/1JS?path=/ooui/packages/acui-ribbon-like/src/UISurfaces/Ribbon/Controls/next/commonRibbonButtonStyles.styles.ts&version=GBmain&line=349&lineEnd=358&lineStartColumn=1&lineEndColumn=1&lineStyle=plain&_a=contents
        [`& .${menuTextChevronClass}`]: {
            paddingLeft: 0,
            width: 'auto',
        },

        // Fixing High Contrast issue with selected Toggle Buttons
        [`& .${toggleButtonClassNames.icon}`]: {
            forcedColorAdjust: 'unset',
        },
    },
    bottomBarContainerGroupDivider: {
        '& .ribbon-bottom-bar-divider': {
            backgroundColor: tokens.colorNeutralStroke1,
        },
    },
    outerContainer: {
        background: 'none',
        padding: '0',
    },
});

const getCustomStylesV9 = (outerContainer: string): Partial<RibbonBottomBarClassNames> => ({
    outerContainer,
});

export interface CommandRibbonBottomBarProps {
    disableAnimation?: boolean;
    isProjectionPopout: boolean;
    projectionUnderlay?: AppPaneUnderlayViewState;
    selectedTab: RibbonTabDefinitionSLR | RibbonTabDefinitionMLR;
    controlRendererFunction: (c: ControlInMenuRendererFunction) => ControlRendererFunction;
    copilotCommandCenter?: React.ReactNode;
    showRibbonModeToggle?: boolean;
}

export const bottomBarId = 'BottomBar';

export const CommandRibbonBottomBar: React.FunctionComponent<CommandRibbonBottomBarProps> =
    observer(function CommandRibbonBottomBar({
        disableAnimation,
        isProjectionPopout,
        projectionUnderlay,
        selectedTab,
        controlRendererFunction,
        copilotCommandCenter,
        showRibbonModeToggle = true,
    }: CommandRibbonBottomBarProps) {
        const { controlGroups, id, initialScalingSteps, scalingSteps } = selectedTab;
        const controlInMenuRendererFunction = useControlInMenuRendererFunction(isProjectionPopout);

        const { bottomBar, bottomBarContainer, bottomBarContainerGroupDivider, outerContainer } =
            useStyles();

        const isFluentV9Enabled = isFeatureEnabled('mon-ribbon-fluent-v9-ribbon');
        const isBleedThroughEnabled = isCapabilityEnabled(bleedThroughCapability);

        const isAppPaneExpanded = isAppPaneUnderlayExpanded(projectionUnderlay) || false;
        const flexPaneExpanded = isFlexPaneExpanded();
        const isCommandCenterVisible = getIsCommandCenterVisible();

        // A bit of a hack since `paddleProps` and `isAppPaneExpanded` are not related, but:
        // 1. We need `RibbonBottombar` to re-render when a pane opens, closes, or expands, or when
        //    the `isCommandCenterVisible` state changes.
        // 2. `RibbonBottombar` will only re-render when its props change.
        //
        // Using `useMemo`, we redefine `paddleProps` only when any of these variables change
        // (resulting in `RibbonBottomBar` re-rendering).
        const paddleProps: RibbonPaddleProps = React.useMemo(
            () => ({
                disableModeTogglePadding: !isFluentV9Enabled,
                ribbonPaddlesIcon: appIcons.downChevron.icon12,
            }),
            [isAppPaneExpanded, flexPaneExpanded, isCommandCenterVisible]
        );

        const unblockPlt = useBlockPlt('ribbon');

        const containerStylesV8 = classnames(
            bottomBarContainerV8,
            isBleedThroughEnabled && bottomBarContainerV8BleedThrough
        );

        const containerStylesV9 = classnames(
            bottomBarContainer,
            getIsDarkTheme() && bottomBarContainerGroupDivider
        );

        const useTabsterAttribute = useTabsterAttributes({
            focusable: {
                ignoreKeydown: {
                    Tab: true,
                },
            },
        });

        return (
            <div id={bottomBarId} className={isFluentV9Enabled ? bottomBar : bottomBarV8}>
                <div
                    {...useTabsterAttribute}
                    className={isFluentV9Enabled ? containerStylesV9 : containerStylesV8}
                    onKeyDown={onKeyDown}
                >
                    <FluentIconStyleProvider
                        {...(isFluentV9Enabled ? iconStylesFluentV9 : iconStyles)}
                    >
                        <RibbonBottomBar
                            appStrings={getAppStrings()}
                            ariaControlsId={getRibbonBottomBarAriaControlsId(id)}
                            ariaLabeledBy={id}
                            controlGroups={controlGroups}
                            controlRendererFunction={controlRendererFunction(
                                controlInMenuRendererFunction
                            )}
                            controlInMenuRendererFunction={controlInMenuRendererFunction}
                            customStyles={
                                isFluentV9Enabled
                                    ? getCustomStylesV9(outerContainer)
                                    : customStylesV8
                            }
                            customStylingConstants={
                                !isFluentV9Enabled && !isSingleLineRibbon()
                                    ? getRibbonBottomBarStyleConstants()
                                    : undefined
                            }
                            disableAnimations={disableAnimation}
                            id={getRibbonBottomBarId(id)}
                            initialScalingSteps={initialScalingSteps}
                            overflowMenuLauncherIcon={appIcons.overflowMenuLauncher}
                            paddleProps={paddleProps}
                            scalingSteps={scalingSteps}
                            preventShadowRendering={true}
                            disableQuickScaling={true}
                            ribbonModeToggleProps={
                                isFluentV9Enabled && showRibbonModeToggle
                                    ? ribbonModeToggleProps
                                    : undefined
                            }
                            onAfterScalingCompleted={unblockPlt}
                        />
                    </FluentIconStyleProvider>
                </div>
                {!isFluentV9Enabled && showRibbonModeToggle && <RibbonModeMenuContainer />}

                {copilotCommandCenter}
            </div>
        );
    },
    'CommandRibbonBottomBar');

const onKeyDown: React.KeyboardEventHandler = ({ key, target }) => {
    const isCombobox = target instanceof HTMLElement && target.className.includes('fui-Combobox');

    if (
        key === 'ArrowUp' &&
        // When the target is a `Combobox`, `ArrowUp` should have no impact
        // (because `ArrowUp` opens the menu)
        !isCombobox
    ) {
        const selectedTab: HTMLButtonElement | null | undefined = document
            .getElementById('tablist')
            ?.querySelector('button[aria-selected="true"]');

        selectedTab?.focus();
    }
};
